import React, { type FC } from 'react';
import styled, { css } from 'styled-components';

import { Icon, Tooltip } from '@column/column-ui-kit';

import { CopyInput } from '~/components';
import { Flex, Text } from '~/styles';

interface CopyRowProps {
  label: string;
  value: string | React.ReactNode;
  warning?: string;
  isParent?: boolean;
  tooltip?: string;
  variant?: 'secondary' | 'default';
  isMonospaced?: boolean;
  className?: string;
  noBorder?: boolean;
  /** Render the value in a darker, medium-weight face (e.g. to emphasise a balance). */
  emphasizeValue?: boolean;
}

const CopyRowContainer = styled.div<CopyRowProps>`
  container-type: inline-size;
  container-name: copy-row;
  border-top: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};

  &:first-child {
    border-top: none;
  }

  ${({ noBorder }) =>
    noBorder &&
    css`
      border-top: none;
    `}

  ${({ variant }) =>
    variant === 'secondary' &&
    css`
      background-color: ${({ theme }) => theme.secondary.blendToBackground(25)};
    `}
`;

export const CopyRowWrapper = styled.div<CopyRowProps>`
  display: grid;
  grid-template-columns: auto;
  align-items: flex-start;
  line-height: 16px;
  overflow: hidden;

  @container copy-row (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    grid-template-columns: minmax(auto, 220px) auto;
    gap: 16px;
  }

  ${({ isParent }) =>
    isParent &&
    css`
      grid-template-columns: 1fr;
    `}
`;

const LabelWrapper = styled.div`
  padding: 16px 16px 0;

  @container copy-row (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    padding: 16px;
  }
`;

const FieldWrapper = styled.div`
  padding: 4px 4px 8px;

  @container copy-row (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    padding: 8px 8px 8px 24px;
  }
`;

// For rows whose value is custom content (e.g. a chip) instead of a string. Mirrors the horizontal
// text position of a string value — FieldWrapper's padding plus the small CopyInput's own 12px inner
// padding — so custom values line up with the copy inputs in sibling rows.
export const CustomFieldWrapper = styled.div`
  padding: 4px 4px 8px 16px;

  @container copy-row (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    padding: 8px 8px 8px 36px;
  }
`;

const IconWarning = styled(Icon.Warning)`
  --icon-color: ${({ theme }) => theme.warning.background};
  --icon-size: 16px;
`;

const IconQuestionmark = styled(Icon.CircleQuestionmark)`
  --icon-color: ${({ theme }) => theme.secondary.blendToBackground(500)};
  --icon-size: 16px;
`;

const StyledCopyInput = styled(CopyInput)`
  --input-background: transparent;
  --input-border-color: transparent;
  --input-color: ${({ theme }) => theme.secondary.blendToBackground(800)};

  &:hover {
    --input-background: ${({ theme }) => theme.style.inputDefaultBackgroundColor};
    --input-border-color: ${({ theme }) => theme.style.inputDefaultHoveredBorderColor};
  }
`;

// Same size, but a darker color and medium weight — used to give a value (e.g. a balance) more prominence.
const EmphasizedCopyInput = styled(StyledCopyInput)`
  --input-color: ${({ theme }) => theme.foreground};

  input {
    font-weight: 500;
  }
`;

export const CopyRow: FC<CopyRowProps> = (props) => {
  const { className, ...rest } = props;
  return (
    <CopyRowContainer {...rest} className={className}>
      <CopyRowWrapper {...rest}>
        <LabelWrapper>
          <Flex gap={4} alignItems="center">
            <Text as="label" size={14} whiteSpace="nowrap" weight="medium">
              {props.label}
            </Text>
            {props.warning && (
              <Tooltip content={props.warning}>
                <IconWarning />
              </Tooltip>
            )}
            {props.tooltip && (
              <Tooltip content={props.tooltip}>
                <IconQuestionmark />
              </Tooltip>
            )}
          </Flex>
        </LabelWrapper>
        {typeof props.value === 'string' ? (
          <FieldWrapper>
            {props.emphasizeValue ? (
              <EmphasizedCopyInput value={props.value} size="small" isMonospaced={props.isMonospaced} />
            ) : (
              <StyledCopyInput value={props.value} size="small" isMonospaced={props.isMonospaced} />
            )}
          </FieldWrapper>
        ) : (
          props.value
        )}
      </CopyRowWrapper>
    </CopyRowContainer>
  );
};
